import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CheckoutLayout } from '@/routes/CheckoutLayout';
import { CheckRoute } from '@/routes/CheckRoute';
import { LoyaltyRoute } from '@/routes/LoyaltyRoute';
import { PayRoute } from '@/routes/PayRoute';
import { PaymentReturnRoute } from '@/routes/PaymentReturnRoute';
import { PaymentRoute } from '@/routes/PaymentRoute';
import { ScanRoute } from '@/routes/ScanRoute';
import { TipRoute } from '@/routes/TipRoute';
import { InvalidCodeScreen, LoadingScreen } from '@/features/check/StatusScreen';

/*
 * The design preview, development only.
 *
 * `import.meta.env.DEV` is a compile-time constant, so this whole branch — and the fixture data behind it —
 * is eliminated from a production build rather than merely unreachable. Lazily imported so the fixtures are
 * not pulled into the main chunk even in development.
 */
const PreviewRoute = import.meta.env.DEV
    ? lazy(() => import('@/dev/PreviewRoute').then((m) => ({ default: m.PreviewRoute })))
    : null;

/**
 * The guest flow.
 *
 * A guest only ever arrives one way: by scanning the QR printed on their check, which points at `/c/{code}`.
 * That route trades the code for a session and redirects to `/check`, so the code sits in the address bar for
 * one navigation and no longer.
 *
 * There is no home page. Someone who types the domain in has no check to pay, so `/` sends them to `/check`,
 * which either finds a stored session or tells them to scan.
 */
export default function App() {
    return (
        <Routes>
            <Route path="/c/:code" element={<ScanRoute />} />

            {/*
              * The checkout steps share a layout that owns the session, the check and the brand, so they
              * cannot disagree about any of them and moving between steps does not refetch.
              *
              * Separate routes rather than one screen with a step counter: a guest will press the browser's
              * back button, and on a phone it is often the control they trust most.
              */}
            <Route element={<CheckoutLayout />}>
                <Route path="/check" element={<CheckRoute />} />

                {/*
                  * Only reached when the restaurant runs a programme its POS can discount - the check step
                  * decides. A guest who lands here anyway is sent on to the tip rather than shown an error
                  * about a feature they did not ask for.
                  */}
                <Route path="/loyalty" element={<LoyaltyRoute />} />
                <Route path="/tip" element={<TipRoute />} />
                <Route path="/payment" element={<PaymentRoute />} />
                <Route path="/pay" element={<PayRoute />} />

                {/* Where a hosted payment page returns the browser. Server-configured; no query read here. */}
                <Route path="/pay/return" element={<PaymentReturnRoute />} />
            </Route>

            <Route path="/" element={<Navigate to="/check" replace />} />

            {PreviewRoute && (
                <Route
                    path="/__preview"
                    element={
                        <Suspense fallback={<LoadingScreen />}>
                            <PreviewRoute />
                        </Suspense>
                    }
                />
            )}

            <Route path="*" element={<InvalidCodeScreen />} />
        </Routes>
    );
}
